<template>
  <div class="delay-config">
    <div class="delay-config__row">
      <NumberInput
        id="delay-duration"
        label="Duur"
        :model-value="localConfig.duration"
        :min="1"
        :max="maxValue"
        @update:model-value="onDurationChange"
      />
      <SelectInput
        id="delay-unit"
        label="Eenheid"
        :options="unitOptions"
        :model-value="localConfig.unit"
        @update:model-value="onUnitChange"
      />
    </div>

    <p v-if="durationError" class="delay-config__error">
      {{ durationError }}
    </p>

    <div class="delay-config__preview">
      <BaseIcon name="calendar-clock" size="14" class="delay-config__preview-icon" />
      <span>
        De automation pauzeert
        <strong>{{ humanReadableDuration }}</strong>
        voordat de volgende stap wordt uitgevoerd.
      </span>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { DELAY_UNITS, DELAY_MAX_VALUE_PER_UNIT } from '@/constants/automations';

  function clamp(value, min, max) {
    if (Number.isNaN(value)) return min;
    return Math.min(Math.max(value, min), max);
  }

  function normalizeConfig(raw) {
    const unit = DELAY_UNITS.find((u) => u.value === raw?.unit)?.value || 'hours';
    const max = DELAY_MAX_VALUE_PER_UNIT[unit];
    const duration = clamp(parseInt(raw?.duration, 10) || 1, 1, max);
    return { duration, unit };
  }

  export default {
    name: 'DelayConfig',

    components: {
      NumberInput,
      SelectInput,
      BaseIcon,
    },

    props: {
      modelValue: {
        type: Object,
        required: true,
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const localConfig = ref(normalizeConfig(props.modelValue));

      const unitOptions = DELAY_UNITS.map((u) => ({
        value: u.value,
        label: u.label,
      }));

      const maxValue = computed(() => DELAY_MAX_VALUE_PER_UNIT[localConfig.value.unit] || 999);

      const durationError = computed(() => {
        const v = Number(localConfig.value.duration);
        if (!Number.isInteger(v) || v < 1) {
          return 'Duur moet een geheel getal van minimaal 1 zijn.';
        }
        if (v > maxValue.value) {
          const unitLabel = DELAY_UNITS.find(
            (u) => u.value === localConfig.value.unit
          )?.label?.toLowerCase();
          return `Maximaal ${maxValue.value} ${unitLabel} per delay-stap. Gebruik twee delays voor langere wachttijden.`;
        }
        return '';
      });

      const humanReadableDuration = computed(() => {
        const { duration, unit } = localConfig.value;
        const unitDef = DELAY_UNITS.find((u) => u.value === unit);
        if (!unitDef) return `${duration} ${unit}`;
        const label = unitDef.label.toLowerCase();
        // Crude singular: drop trailing 'n' for nl when count == 1
        // (uren → uur, dagen → dag, weken → week, minuten → minuut)
        if (duration === 1) {
          const singular = {
            minutes: 'minuut',
            hours: 'uur',
            days: 'dag',
            weeks: 'week',
          }[unit];
          return `1 ${singular || label}`;
        }
        return `${duration} ${label}`;
      });

      const emitUpdate = () => {
        emit('update:modelValue', { ...localConfig.value });
      };

      const onDurationChange = (value) => {
        const parsed = parseInt(value, 10);
        localConfig.value.duration = Number.isNaN(parsed) ? 1 : parsed;
        emitUpdate();
      };

      const onUnitChange = (value) => {
        localConfig.value.unit = value;
        const max = DELAY_MAX_VALUE_PER_UNIT[value] || 999;
        if (localConfig.value.duration > max) {
          localConfig.value.duration = max;
        }
        emitUpdate();
      };

      watch(
        () => props.modelValue,
        (newVal) => {
          const next = normalizeConfig(newVal);
          if (
            next.duration !== localConfig.value.duration ||
            next.unit !== localConfig.value.unit
          ) {
            localConfig.value = next;
          }
        },
        { deep: true }
      );

      return {
        localConfig,
        unitOptions,
        maxValue,
        durationError,
        humanReadableDuration,
        onDurationChange,
        onUnitChange,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .delay-config {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      align-items: end;
    }

    &__error {
      margin: 0;
      font-size: 0.75rem;
      color: var(--red-600);
    }

    &__preview {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.6rem 0.75rem;
      background: var(--brand-50);
      border: 1px solid var(--brand-200);
      border-radius: 6px;
      font-size: 0.8rem;
      color: var(--brand-700);
      line-height: 1.4;

      strong {
        font-weight: 600;
      }
    }

    &__preview-icon {
      flex-shrink: 0;
      margin-top: 1px;
      color: var(--brand-500);
    }
  }
</style>
